// ============================================================================
// FNE-BRIDGE - Store Authentification (Zustand)
// ============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, Company, Establishment, PointOfSale } from '../types/models';
import type { AuthTokens } from '../types/api';

// ============================================================================
// Types du state
// ============================================================================

interface AuthState {
  // State
  user: User | null;
  company: Company | null;
  establishments: Establishment[];
  pointsOfSale: PointOfSale[];
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  // OTP flow
  otpSessionId: string | null;
  otpMaskedEmail: string | null;
  requiresOtp: boolean;
  // First login
  mustChangePassword: boolean;
  // License
  licenseExpired: boolean;

  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setOtpSession: (sessionId: string, maskedEmail: string) => void;
  clearOtpSession: () => void;
  setAuth: (user: User, company: Company, tokens: AuthTokens) => void;
  setEstablishments: (establishments: Establishment[]) => void;
  setPointsOfSale: (pointsOfSale: PointOfSale[]) => void;
  updateUser: (updates: Partial<User>) => void;
  updateCompany: (updates: Partial<Company>) => void;
  updateTokens: (tokens: AuthTokens) => void;
  setMustChangePassword: (must: boolean) => void;
  setLicenseExpired: (expired: boolean) => void;
  logout: () => void;
  reset: () => void;
}

// ============================================================================
// State initial
// ============================================================================

const initialState = {
  user: null,
  company: null,
  establishments: [],
  pointsOfSale: [],
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  otpSessionId: null,
  otpMaskedEmail: null,
  requiresOtp: false,
  mustChangePassword: false,
  licenseExpired: false,
};

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,

      setLoading: (isLoading) => set({ isLoading, error: null }),

      setError: (error) => set({ error, isLoading: false }),

      setOtpSession: (otpSessionId, otpMaskedEmail) =>
        set({ otpSessionId, otpMaskedEmail, requiresOtp: true, isLoading: false }),

      clearOtpSession: () =>
        set({ otpSessionId: null, otpMaskedEmail: null, requiresOtp: false }),

      setAuth: (user, company, tokens) =>
        set({
          user,
          company,
          tokens,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          otpSessionId: null,
          otpMaskedEmail: null,
          requiresOtp: false,
          mustChangePassword: user.doit_changer_mdp,
        }),

      setLicenseExpired: (licenseExpired) => set({ licenseExpired }),

      setEstablishments: (establishments) => set({ establishments }),

      setPointsOfSale: (pointsOfSale) => set({ pointsOfSale }),

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      updateCompany: (updates) =>
        set((state) => ({
          company: state.company ? { ...state.company, ...updates } : null,
        })),

      updateTokens: (tokens) => set({ tokens }),

      setMustChangePassword: (mustChangePassword) => set({ mustChangePassword }),

      logout: () => set({ ...initialState }),

      reset: () => set({ ...initialState }),
    }),
    {
      name: 'fne-bridge-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        tokens: state.tokens,
        user: state.user,
        company: state.company,
        isAuthenticated: state.isAuthenticated,
        licenseExpired: state.licenseExpired,
      }),
    }
  )
);
